
import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { StartupAccount, StartupCommunity } from "@/types/startup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Users, Calendar, MessageSquare, Settings } from "lucide-react";
import StartupCommunityChat from "./StartupCommunityChat";

interface StartupCommunitiesProps {
  startupAccount: StartupAccount;
}

const StartupCommunities: React.FC<StartupCommunitiesProps> = ({ startupAccount }) => {
  const { toast } = useToast();
  const [communities, setCommunities] = useState<StartupCommunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<StartupCommunity | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    is_visible_to_teens: true
  });

  useEffect(() => {
    fetchCommunities();
  }, [startupAccount.id]);

  const fetchCommunities = async () => {
    try {
      const { data, error } = await supabase
        .from("startup_communities")
        .select("*")
        .eq("startup_account_id", startupAccount.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCommunities(data || []);
    } catch (error) {
      console.error("Error fetching communities:", error);
      toast({
        title: "Error",
        description: "Failed to load communities",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.description.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);

      const { error } = await supabase
        .from("startup_communities")
        .insert({
          startup_account_id: startupAccount.id,
          name: formData.name.trim(),
          description: formData.description.trim(),
          category: formData.category.trim() || null,
          is_visible_to_teens: formData.is_visible_to_teens,
          admin_user_id: startupAccount.user_id
        });

      if (error) throw error;

      toast({
        title: "Community Created",
        description: "Your startup community has been created successfully"
      });

      setFormData({ name: "", description: "", category: "", is_visible_to_teens: true });
      setDialogOpen(false);
      fetchCommunities();
    } catch (error) {
      console.error("Error creating community:", error);
      toast({
        title: "Error",
        description: "Failed to create community",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  if (selectedCommunity) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={() => setSelectedCommunity(null)}
            className="border-[#FF8C00] text-[#FF8C00] hover:bg-[#FF8C00] hover:text-white"
          >
            ← Back to Communities
          </Button>
        </div>
        <StartupCommunityChat 
          communityId={selectedCommunity.id}
          communityName={selectedCommunity.name}
          isAdmin={true}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#FF8C00]">Communities</h2>
          <p className="text-muted-foreground">Manage your startup communities and engage with teens</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF8C00] hover:bg-orange-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Create Community
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Community</DialogTitle>
              <DialogDescription>
                Create a community for teens interested in your startup
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Community Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter community name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your community..."
                  rows={3}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="e.g., Technology, Education"
                />
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" disabled={submitting} className="flex-1 bg-[#FF8C00] hover:bg-orange-600 text-white">
                  {submitting ? "Creating..." : "Create Community"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {communities.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No Communities Yet</CardTitle>
            <CardDescription>
              Create your first community to start engaging with teen users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#FF8C00] hover:bg-orange-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Community
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {communities.map((community) => (
            <Card key={community.id} className="border-[#FF8C00]/20 hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-[#FF8C00]">
                      {community.name}
                      {community.category && (
                        <Badge variant="secondary" className="bg-[#FF8C00]/10 text-[#FF8C00]">
                          {community.category}
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {community.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {community.member_count} members
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Created {new Date(community.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSelectedCommunity(community)}
                    className="border-[#FF8C00] text-[#FF8C00] hover:bg-[#FF8C00] hover:text-white"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Open Chat
                  </Button>
                  <Button variant="outline" size="sm">
                    <Settings className="h-4 w-4 mr-1" />
                    Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default StartupCommunities;
